import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Instagram, Mail, Phone } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "@/lib/site";
import { EASE } from "@/components/Motion";

function Logo() {
  return (
    <Link to="/" data-testid="nav-home-logo" className="flex items-center gap-3" aria-label={`${BUSINESS.name} — home`}>
      <img
        src="/images/logo.png"
        alt={`${BUSINESS.name} logo`}
        className="h-10 w-10 rounded-lg object-contain"
        width="40"
        height="40"
      />
      <span className="hidden font-display text-[13px] font-bold uppercase leading-tight tracking-[0.18em] text-white sm:block">
        Skies The Limit
        <span className="block text-[10px] font-medium tracking-[0.3em] text-gold">Creations LLC</span>
      </span>
    </Link>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const testId = (l) => `nav-${l.to === "/" ? "home" : l.to.slice(1)}-link`;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color] duration-300 ${
          scrolled ? "border-b border-white/10 bg-ink/85 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
        }`}
      >
        <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8" aria-label="Main">
          <Logo />
          <div className="hidden items-center gap-8 lg:flex">
            {NAV_LINKS.slice(1).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={testId(l)}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-200 ${isActive ? "text-gold" : "text-mist hover:text-white"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/quote" data-testid="nav-quote-cta-button" className="btn-primary !px-5 !py-2.5 text-xs">
              Request a Quote
            </Link>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            aria-expanded={open}
            data-testid="nav-mobile-menu-button"
            className="rounded-full border border-white/15 p-2.5 text-white lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 flex flex-col bg-ink/[0.98] backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex h-[72px] items-center justify-between px-5">
              <Logo />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="nav-mobile-close-button"
                className="rounded-full border border-white/15 p-2.5 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 + i * 0.05, duration: 0.45, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${testId(l)}`}
                    className={({ isActive }) =>
                      `block border-b border-white/5 py-4 font-display text-3xl font-bold tracking-tight ${
                        isActive ? "text-gold" : "text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.36, duration: 0.45, ease: EASE }}
                className="mt-8"
              >
                <Link to="/quote" data-testid="mobile-nav-quote-cta-button" className="btn-primary w-full">
                  Request a Quote <ArrowUpRight className="h-4 w-4" />
                </Link>
                <div className="mt-8 flex flex-col gap-3 text-sm text-mist">
                  <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-2" data-testid="mobile-nav-email-link">
                    <Mail className="h-4 w-4 text-gold" /> {BUSINESS.email}
                  </a>
                  <a href={BUSINESS.phoneHref} className="flex items-center gap-2" data-testid="mobile-nav-phone-link">
                    <Phone className="h-4 w-4 text-gold" /> {BUSINESS.phoneDisplay}
                  </a>
                  <a
                    href={BUSINESS.instagram.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2"
                    data-testid="mobile-nav-instagram-link"
                  >
                    <Instagram className="h-4 w-4 text-gold" /> {BUSINESS.instagram.label}
                  </a>
                </div>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
